<template>
  <!-- Multilingual v3 (founder 19.10.2026): the project's languages, add, online and remove. Sysadmin-only. -->
  <div>
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <h3>{{ $t('settings.project.languages.name') }}</h3>
        <hr>
        <p>{{ $t('settings.project.languages.description') }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <p v-if="!main" class="lang-note" data-test="languages-no-main">{{ $t('settings.project.languages.noMain') }}</p>
        <ul v-else class="lang-list" data-test="languages-list">
          <li class="lang-row">
            <span class="lang-code">{{ main.handle.toUpperCase() }}</span>
            <span class="lang-name">{{ nameOf(main.handle) }}</span>
            <span class="lang-note">{{ $t('webmag.languageMain') }}</span>
          </li>
          <li v-for="l in extra" :key="l.project_language_id" class="lang-row" :data-test="`languages-row-${l.language.handle}`">
            <span class="lang-code">{{ l.language.handle.toUpperCase() }}</span>
            <span class="lang-name">{{ nameOf(l.language.handle) }}</span>
            <b-form-checkbox
              switch
              :checked="l.online"
              :data-test="`languages-online-${l.language.handle}`"
              @change="(on) => setOnline(l, on)"
            >{{ $t('webmag.languageOnline') }}</b-form-checkbox>
            <b-button
              variant="link"
              class="lang-remove"
              :data-test="`languages-remove-${l.language.handle}`"
              @click="remove(l)"
            >{{ $t('settings.project.languages.remove') }}</b-button>
          </li>
        </ul>
        <div v-if="main" class="lang-add">
          <b-form-select v-model="newLanguage" :options="addable" data-test="languages-add-select">
            <template #first><b-form-select-option :value="null" disabled>{{ $t('webmag.languageChoose') }}</b-form-select-option></template>
          </b-form-select>
          <b-button variant="primary" :disabled="!newLanguage" data-test="languages-add" @click="add">
            {{ $t('webmag.languageAddSave') }}
          </b-button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import GetProjectLanguages from '@/graphQlQueries/queries/getProjectLanguages';
import AddProjectLanguage from '@/graphQlQueries/mutations/addProjectLanguage';
import SetProjectLanguageOnline from '@/graphQlQueries/mutations/setProjectLanguageOnline';
import DeleteProjectLanguage from '@/graphQlQueries/mutations/deleteProjectLanguage';

export default {
  name: 'ProjectSettingsLanguages',
  props: {
    projectId: { type: Number, required: true },
  },
  data: () => ({
    data: null,
    newLanguage: null,
  }),
  apollo: {
    data: {
      query: GetProjectLanguages,
      fetchPolicy: 'no-cache',
      variables() {
        return { projectId: this.projectId };
      },
      update(data) {
        return data;
      },
    },
  },
  computed: {
    main() {
      return (this.data && this.data.projects_by_pk && this.data.projects_by_pk.language) || null;
    },
    extra() {
      const main = this.main && this.main.handle;
      return ((this.data && this.data.project_languages) || []).filter((l) => l.language && l.language.handle !== main);
    },
    /** Languages the project does not have yet, by name. */
    addable() {
      const taken = new Set([this.main && this.main.handle, ...this.extra.map((l) => l.language.handle)]);
      return ((this.data && this.data.languages) || [])
        .filter((l) => l.handle && !taken.has(l.handle))
        .map((l) => ({ value: l.language_id, text: `${this.nameOf(l.handle)} (${l.handle.toUpperCase()})` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
  },
  methods: {
    nameOf(handle) {
      try {
        return new Intl.DisplayNames([this.$i18n ? this.$i18n.locale : 'de'], { type: 'language' }).of(handle) || handle;
      } catch (e) {
        return handle;
      }
    },
    async run(mutation, variables) {
      try {
        await this.$apollo.mutate({ mutation, variables });
      } catch (e) {
        console.error('[ProjectSettingsLanguages] not saved', e);
        this.$bvToast.toast(this.$t('webmag.languageAddFailed'), { variant: 'danger', solid: true });
      }
      await this.$apollo.queries.data.refetch();
    },
    add() {
      if (!this.newLanguage) return;
      const languageId = this.newLanguage;
      this.newLanguage = null;
      this.run(AddProjectLanguage, { projectId: this.projectId, languageId, sort: this.extra.length + 1 });
    },
    setOnline(l, online) {
      this.run(SetProjectLanguageOnline, { projectId: this.projectId, languageId: l.language.language_id, online: !!online });
    },
    async remove(l) {
      const ok = await this.$bvModal.msgBoxConfirm(this.$t('settings.project.languages.removeConfirm', { name: this.nameOf(l.language.handle) }), {
        okTitle: this.$t('settings.project.languages.remove'),
        okVariant: 'danger',
        cancelTitle: this.$t('buttons.cancel'),
        centered: true,
      });
      if (ok) this.run(DeleteProjectLanguage, { projectLanguageId: l.project_language_id });
    },
  },
};
</script>

<style scoped>
/* On the settings' dark background (wm-settings). */
.lang-list {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.lang-row {
  display: flex;
  align-items: center;
  gap: 24px;
  min-height: 52px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.lang-code {
  width: 32px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

.lang-name {
  flex: 1;
  font-weight: 500;
}

.lang-note {
  color: rgba(255, 255, 255, 0.5);
}

/* The switch label reads as text, not as the settings' small caps labels. */
.lang-row ::v-deep .custom-control-label {
  font-size: 14px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  color: rgba(255, 255, 255, 0.7);
}

.lang-remove {
  padding: 0;
  color: #fca5a5;
}

.lang-remove:hover {
  color: #fecaca;
}

.lang-add {
  display: flex;
  gap: 8px;
  max-width: 420px;
}
</style>
